"use client";

import React from "react";
import {
  Box,
  Flex,
  VStack,
  Divider,
  Button,
  Tooltip,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarItem from "../components/icons/SidebarItem";
import SpacesLogo from "../components/icons/SpacesLogo";
import DashboardIcon from "../components/icons/DashboardIcon";
import MeetingsIcon from "../components/icons/MeetingsIcon";
import ClientsIcon from "../components/icons/ClientsIcon";
import SalesTeamIcon from "../components/icons/SalesTeamIcon";
import { ExternalLinkIcon } from "@chakra-ui/icons";

// Define props interface
interface SidebarProps {
  onLogout: () => void;
  onLogoutAll:()=>void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLogout,onLogoutAll }) => {
  const navigate = useNavigate();
  const [, startTransition] = React.useTransition();
  const location = useLocation();
  const path = location.pathname;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isLogoutOpen, onOpen: onLogoutOpen, onClose: onLogoutClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const logoutCancelRef = React.useRef<HTMLButtonElement>(null);

  const handleLogoutAllClick = () => {
    onOpen();
  };

  const handleConfirmLogoutAll = () => {
    onLogoutAll();
    onClose();
  };

  const handleLogoutClick = () => {
    onLogoutOpen();
  };

  const handleConfirmLogout = () => {
    onLogout();
    onLogoutClose();
  };
  // Get the correct active item based on path
  let activeItem = "dashboard";

  if (path.startsWith("/dashboard")) {
    activeItem = "dashboard";
  } else if (path.startsWith("/meetings") || path.startsWith("/meeting-details")) {
    activeItem = "meetings";
  } else if (path.startsWith("/clients") || path.startsWith("/client-details") || path.startsWith("/client-profile") || path.startsWith("/client/")) {
    activeItem = "clients";
  } else if (path.startsWith("/sales-team") || path.startsWith("/sales-executive")) {
    activeItem = "salesteam";
  }

  const navItems = [
    { id: "dashboard", icon: <DashboardIcon />, label: "Dashboard", path: "/dashboard" },
    { id: "meetings", icon: <MeetingsIcon />, label: "Meetings", path: "/meetings" },
    { id: "clients", icon: <ClientsIcon />, label: "Clients", path: "/clients" },
    { id: "salesteam", icon: <SalesTeamIcon />, label: "Sales Team", path: "/sales-team" },
  ];

  const handleNavigation = (path: string) => {
    // Mark the route change as a non-urgent transition. Pages like the
    // Dashboard mount a large tree of charts at once (now even more
    // concentrated since their data resolves instantly from cache), which
    // could block the click. A transition lets React keep the sidebar
    // responsive and render the heavy page without janking the navigation.
    startTransition(() => {
      navigate(path);
    });
  };

  return (
    <Box
      as="aside"
      bg="gray.800"
      w="228px"
      h="100%"
      display="flex"
      flexDirection="column"
    >
      <Flex direction="column" pt={12} pr={6} pb={0} pl={4}>
        <Box mb={4}>
          <SpacesLogo />
        </Box>
        
     
        
       
      </Flex>
      <Flex
        as="nav"
        direction="column"
        gap={2}
        px={1}
        flex="1"
        overflowY="auto"
        mt={2}
      >
        {navItems.map((item) => (
          <SidebarItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            isActive={activeItem === item.id}
            onClick={() => handleNavigation(item.path)}
          />
        ))}
      </Flex>

      <VStack mt="auto" p={4} alignItems="stretch">
        <Divider my={2} borderColor="gray.700" />
        <Button
          variant="ghost"
          justifyContent="start"
          onClick={handleLogoutClick}
          color="gray.400"
          _hover={{ bg: "gray.700", color: "white" }}
          w="full"
          leftIcon={<ExternalLinkIcon w={4} h={4} />}
        >
          Logout
        </Button>
        <Tooltip 
          label="Logout All Sales Person" 
          placement="top"
          hasArrow
          bg="gray.700"
          color="white"
          padding="6px"
          borderRadius={"md"}
        >
          <Button
            variant="ghost"
            justifyContent="start"
            onClick={handleLogoutAllClick}
            color="gray.400"
            _hover={{ bg: "gray.700", color: "red.300" }}
            w="full"
            leftIcon={<ExternalLinkIcon w={4} h={4} />}
          >
            Logout All
          </Button>
        </Tooltip>
      </VStack>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            bg="gray.800"
            border="1px solid"
            borderColor="gray.700"
            color="white"
          >
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">
              Logout All Sales People
            </AlertDialogHeader>
            <AlertDialogBody color="gray.200">
              Are you sure you want to log out all sales people? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmLogoutAll} ml={3}>
                Logout All
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isLogoutOpen}
        leastDestructiveRef={logoutCancelRef}
        onClose={onLogoutClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            bg="gray.800"
            border="1px solid"
            borderColor="gray.700"
            color="white"
          >
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">
              Confirm Logout
            </AlertDialogHeader>
            <AlertDialogBody color="gray.200">
              Are you sure you want to log out?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={logoutCancelRef} onClick={onLogoutClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmLogout} ml={3}>
                Logout
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Sidebar;
